import type { StampAtomicCoverage, StampDetail } from "@peer-start/platform-contracts";
import { Card, CardHeader, Disclosure } from "@peer-start/ui";
import { AlertTriangle, Check, Circle, Minus, Network, ShieldCheck } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

const statusClass = {
  covered: "border-success bg-success text-success-foreground",
  partial: "border-warning bg-warning text-warning-foreground",
  uncovered: "border-border bg-muted text-muted-foreground",
} as const;

const StatusIcon = ({ state }: { state: keyof typeof statusClass }) =>
  state === "covered" ? (
    <Check aria-hidden="true" className="size-4" />
  ) : state === "partial" ? (
    <Minus aria-hidden="true" className="size-4" />
  ) : (
    <Circle aria-hidden="true" className="size-3" />
  );

function CoverageBar({ coverage, compact = false }: { coverage: StampAtomicCoverage; compact?: boolean }) {
  const { t } = useTranslation();
  const total = coverage.covered + coverage.partial + coverage.uncovered;
  const segments = [
    { count: coverage.covered, className: "bg-success" },
    { count: coverage.partial, className: "bg-warning" },
    { count: coverage.uncovered, className: "bg-muted" },
  ];
  return (
    <div
      aria-label={t("passportVig.coverage.visualLabel", {
        covered: coverage.covered,
        partial: coverage.partial,
        uncovered: coverage.uncovered,
      })}
      className={`flex overflow-hidden rounded-full bg-muted ${compact ? "h-1.5" : "h-2.5"}`}
      role="img"
    >
      {segments.map((segment, index) =>
        segment.count ? (
          <span
            aria-hidden="true"
            className={segment.className}
            key={segment.className}
            style={{ width: `${total ? (segment.count / total) * 100 : index === 2 ? 100 : 0}%` }}
          />
        ) : null,
      )}
    </div>
  );
}

export function StampAtomicCoverage({
  coverage,
  children,
}: {
  coverage: StampAtomicCoverage;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Card padding="compact">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <CardHeader title={t("passportVig.coverage.title")} />
        <div
          className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium ${
            coverage.policyMet ? "bg-success text-success-foreground" : "bg-warning text-warning-foreground"
          }`}
        >
          {coverage.policyMet ? (
            <ShieldCheck aria-hidden="true" className="size-5" />
          ) : (
            <AlertTriangle aria-hidden="true" className="size-5" />
          )}
          {t(`passportVig.coverage.${coverage.policyMet ? "policyMet" : "policyOpen"}`)}
        </div>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        {t("passportVig.coverage.summary", {
          percent: coverage.percent,
          threshold: coverage.thresholdPercent,
        })}
      </p>
      <div className="mt-3">
        <CoverageBar coverage={coverage} />
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {(["covered", "partial", "uncovered"] as const).map((state) => (
          <span className="flex items-center gap-2" key={state}>
            <span className={`size-2.5 rounded-full border ${statusClass[state]}`} />
            {t(`passportVig.coverage.${state}`, { count: coverage[state] })}
          </span>
        ))}
      </div>
      {!coverage.mandatoryMet ? (
        <p className="mt-4 text-sm font-medium text-warning-foreground">
          {t("passportVig.coverage.mandatoryOpen", {
            count: coverage.mandatoryBlockerIds.length,
          })}
        </p>
      ) : null}
      {children ? <div className="mt-4 border-t border-border pt-4">{children}</div> : null}
    </Card>
  );
}

export function StampCoverageStrip({ coverage }: { coverage: StampAtomicCoverage }) {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <div className="min-w-0 flex-1">
        <CoverageBar compact coverage={coverage} />
      </div>
      <span className="w-9 text-right text-xs font-medium text-muted-foreground">{coverage.percent}%</span>
    </div>
  );
}

export function StampAdvancedMatrix({ detail }: { detail: StampDetail }) {
  const { t } = useTranslation();
  const progressById = new Map(
    detail.progress.atomicCoverage.requirements.map((entry) => [entry.requirementId, entry]),
  );
  return (
    <Disclosure
      controlId="passport.advanced.toggle"
      title={
        <span className="flex items-center gap-2">
          <Network aria-hidden="true" className="size-4 text-primary" />
          {t("passportVig.advanced.title")}
        </span>
      }
      description={t("passportVig.advanced.description")}
    >
      <div className="mt-5 overflow-x-auto rounded-xl border border-border">
        <table className="min-w-full border-collapse text-left text-sm">
          <thead className="bg-card">
            <tr>
              <th className="sticky left-0 min-w-72 bg-card p-3">{t("passportVig.advanced.requirement")}</th>
              <th className="p-3 text-center">{t("passportVig.advanced.person")}</th>
              {detail.learningResources.map((resource) => (
                <th className="min-w-36 p-3 text-center" key={resource.id} title={resource.title}>
                  {resource.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {detail.criteria.map((criterion) => {
              const progress = progressById.get(criterion.id);
              const sourceId = criterion.provenance[0]?.sourceId ?? criterion.id;
              return (
                <tr className="border-t border-border" key={criterion.id}>
                  <th className="sticky left-0 bg-background p-3 font-normal">
                    <span className="font-mono text-xs text-muted-foreground">{sourceId}</span>
                    <span className="mt-1 block font-medium">{criterion.title}</span>
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {t(`passportVig.criteria.${criterion.kind}`)}
                    </span>
                  </th>
                  <td className="p-3 text-center">
                    {progress ? (
                      <span
                        aria-label={t(`passportVig.coverage.state.${progress.state}`)}
                        className={`mx-auto grid size-7 place-items-center rounded-md border ${statusClass[progress.state]}`}
                        title={progress.sourceIds.join(", ") || undefined}
                      >
                        <StatusIcon state={progress.state} />
                      </span>
                    ) : null}
                  </td>
                  {detail.learningResources.map((resource) => {
                    const mapping = resource.coverage.find((entry) => entry.criterionId === criterion.id);
                    const state = mapping?.score === 3 ? "covered" : mapping ? "partial" : "uncovered";
                    return (
                      <td className="p-3 text-center" key={resource.id}>
                        <span
                          aria-label={t(
                            mapping
                              ? `passportVig.coverage.strength.${mapping.strength}`
                              : "passportVig.coverage.strength.none",
                          )}
                          className={`mx-auto grid size-7 place-items-center rounded-md border ${statusClass[state]}`}
                          title={mapping?.objectiveIds.join(", ")}
                        >
                          <StatusIcon state={state} />
                        </span>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {detail.atomicRelations.length ? (
        <div className="mt-5">
          <h3 className="font-medium">{t("passportVig.advanced.relations")}</h3>
          <ul className="mt-2 space-y-2 text-sm text-muted-foreground">
            {detail.atomicRelations.map((relation) => (
              <li key={relation.id}>
                <span className="font-mono text-xs">{relation.fromRequirementId}</span> →{" "}
                <span className="font-mono text-xs">{relation.toRequirementId}</span>: {relation.explanation}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Disclosure>
  );
}
